// feature/notion-chrome — frosted glass tooltip primitive. Used by collapsed sidebar
// nav items (Pattern E) to surface labels on hover. 600ms intent delay so
// it never flickers when the mouse just brushes through.
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHoverIntent } from "../hooks/useHoverIntent";

export default function Tooltip({ children, label, side = "right", delay = 600, disabled = false }) {
  const wrapRef = useRef(null);
  const intent = useHoverIntent(delay, 100);

  if (disabled || !label) {
    return (
      <span ref={wrapRef} style={{ display: "contents" }}>{children}</span>
    );
  }

  return (
    <>
      <span
        ref={wrapRef}
        onMouseEnter={intent.onMouseEnter}
        onMouseLeave={intent.onMouseLeave}
        style={{ display: "contents" }}
      >
        {children}
      </span>
      {intent.isFocused && <TooltipBubble triggerRef={wrapRef} side={side} label={label} />}
    </>
  );
}

function TooltipBubble({ triggerRef, side, label }) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    // The wrapper has display: contents — it has no box. Use the first
    // element child as the positioning anchor.
    const anchor = triggerRef.current?.firstElementChild || triggerRef.current;
    if (!anchor || !anchor.getBoundingClientRect) return;
    const r = anchor.getBoundingClientRect();
    let top, left;
    if (side === "right") {
      top = r.top + r.height / 2;
      left = r.right + 8;
    } else if (side === "left") {
      top = r.top + r.height / 2;
      left = r.left - 8;
    } else if (side === "bottom") {
      top = r.bottom + 8;
      left = r.left + r.width / 2;
    } else { // top
      top = r.top - 8;
      left = r.left + r.width / 2;
    }
    setPos({ top, left, side });
  }, [triggerRef, side]);

  if (!pos) return null;
  const style = {
    top: pos.top,
    left: pos.left,
    transform:
      pos.side === "right" ? "translateY(-50%)" :
      pos.side === "left"  ? "translate(-100%, -50%)" :
      pos.side === "top"   ? "translate(-50%, -100%)" :
                              "translateX(-50%)",
  };
  return createPortal(
    <div className="tooltip-content" style={style}>{label}</div>,
    document.body
  );
}
